import React from 'react';

import {
	fetchEntertainmentContent,
} from '../util/entertainment.js';

import {
	deleteFromCache,
	preCache,
} from '../ServiceWorker.js';

class EntertainmentProviderComponent extends React.Component {
	static providerName = 'entertainment';

	constructor(props) {
		super(props);

		this.state = {
			activeSlides: [],
			cachedSlides: [],
			isCaching: false,			
			previousActiveSlides: [], // Track previous active slides for safe deletion
			deleteSlides: [], // Slides to delete after caching
		};

		this.fetchTimer = null;
		this.shouldUpdate = true;
		this.fetchTimes = {};
		this.cachingQueue = [];
		this.activeCachingQueue = []; // Queue for caching active slides

		this.providerID = `PROVIDER_ENTERTAINMENT_${props.component_name}`;
	}

	componentDidMount() {
		const {
			updateSlideshowProvider,
			polling_rate = 60,
		} = this.props;

		// don't initialize if we're not in a slideshow
		if (!updateSlideshowProvider) {
			return;
		}

		this.abortController = new AbortController();
		this.initUpdate();

		this.fetchTimer = setInterval(() => {
			this.initUpdate();
		}, polling_rate * 60 * 1000);
	}

	componentDidUpdate(prevProps) {
		const {
			categories,
			polling_rate = 60,
			number_of_assets,
			order,
			resolution,
			updateSlideshowProvider,
		} = this.props;

		// don't initialize if we're not in a slideshow
		if (!updateSlideshowProvider) {
			return;
		}

		if (categories !== prevProps.categories
			|| order !== prevProps.order
			|| polling_rate !== prevProps.polling_rate
			|| number_of_assets !== prevProps.number_of_assets
			|| resolution !== prevProps.resolution) {
			clearInterval(this.fetchTimer);
			this.initUpdate();

			this.fetchTimer = setInterval(() => {
				this.initUpdate();
			}, polling_rate * 60 * 1000);
		}
	}

	componentWillUnmount() {
		console.log('BROOD::ENTERTAINMENT::componentWillUnmount:', this.props.component_name);

		if (this.abortController) {
			this.abortController.abort();
		}
		clearInterval(this.fetchTimer);
		// Clear any pending caching operations
		this.cachingQueue = [];
	}

	fetchTimeTooEarly() {
		const {
			component_name: componentUUID,
			polling_rate = 60,
		} = this.props;

		const now = Date.now();
		const lastFetchTime = this.fetchTimes[componentUUID] || -1;
		if (lastFetchTime === -1) {
			return false;
		}

		const timeSinceLastFetch = now - lastFetchTime;
		const nextFetchTime = (polling_rate * 60 * 1000) / 2; // Half the polling rate

		if (timeSinceLastFetch >= nextFetchTime) {
			return false;
		}

		return true;
	}

	async initUpdate() {
		const {
			updateSlideshowProvider,
			component_name: componentUUID,
		} = this.props;

		// Check if we're fetching too frequently
		if (this.fetchTimeTooEarly()) {
			console.log('BROOD::ENTERTAINMENT::SKIP(fetchTimeTooEarly):', componentUUID, this.fetchTimes[componentUUID]);
			return;
		}

		this.fetchTimes[componentUUID] = Date.now();

		const { activeSlides, cachedSlides, previousActiveSlides } = this.state;

		let slidesToUse = [];

		// First, check if we have cached slides ready to use
		if (cachedSlides.length > 0) {
			console.log('BROOD::ENTERTAINMENT::Using cached slides:', cachedSlides.length);
			slidesToUse = cachedSlides;

			// Move cached slides to active and clear cached
			this.setState({
				previousActiveSlides: activeSlides, // Store for cleanup
				activeSlides: cachedSlides,
				deleteSlides: previousActiveSlides, // Slides to delete after caching
				cachedSlides: [],				
			});

		} else if (activeSlides.length > 0) {
			// Keep using current active slides
			console.log('BROOD::ENTERTAINMENT::Using existing active slides:', activeSlides.length);
			slidesToUse = activeSlides;
		} else {
			// No slides available, fetch fresh ones
			try {
				slidesToUse = await this.fetchSlides();

				// Check if component was unmounted during fetch
				if (this.abortController?.signal?.aborted) {
					return;
				}

				// Initialize cache status for all slides
				// Cache uncached slides in background (non-blocking)
				if (slidesToUse.length > 0) {
					console.log('BROOD::ENTERTAINMENT::Starting background caching for', slidesToUse.length, 'active slides');
					this.startActiveSlideCaching(slidesToUse);
				}

				// Process slides for the slideshow (add provider reference and skip flag)
				const processedSlides = slidesToUse.map(slide => ({
					...slide,
					provider: this,
					skip: true
				}));

				this.setState({
					activeSlides: processedSlides,
				});

				slidesToUse = processedSlides;
			} catch (err) {
				console.error('BROOD::ENTERTAINMENT::ERROR_initUpdate:', err);
				slidesToUse = [];
			}
		}

		// Update the slideshow with current slides
		const newProvider = this.makeProvider(slidesToUse);
		updateSlideshowProvider(newProvider);

		console.log('BROOD::ENTERTAINMENT::initUpdate complete:', componentUUID);

		// Start background caching of next batch if not already caching
		if (!this.state.isCaching && this.state.cachedSlides.length === 0) {
			this.startBackgroundCaching();
		}
	}

	async fetchSlides() {
		const {
			component_name: componentUUID,
		} = this.props;

		try {
			const slides = await fetchEntertainmentContent(componentUUID);
			console.log('BROOD::ENTERTAINMENT::fetchSlides:', slides);
			// Tag every entertainment slide with convertFormat=false so the Img/Video
			// wrappers skip .webp/.webm probing — entertainment assets live on
			// third-party CDNs we don't control, and the probe is a guaranteed 404.
			// Applied here (not at the processedSlides map below) because slides also
			// flow through cachedSlides → activeSlides paths that bypass that map.
			return (slides || []).map(slide => ({
				...slide,
				data: { ...(slide.data || {}), convertFormat: false }
			}));
		} catch (err) {
			console.error('BROOD::ENTERTAINMENT::ERROR_fetchSlides:', err);
			return this.state.activeSlides; // Return current active slides on error
		}
	}

	async startBackgroundCaching() {
		if (this.state.isCaching || this.abortController?.signal?.aborted) {
			return; // Already caching or unmounted
		}

		await this.deleteCachedSlides();

		if (this.abortController?.signal?.aborted) {
			return;
		}

		console.log('BROOD::ENTERTAINMENT::Starting background caching');
		this.setState({ isCaching: true });

		try {
			// Fetch next batch of slides
			const nextSlides = await this.fetchSlides();

			if (nextSlides.length > 0) {
				console.log('BROOD::ENTERTAINMENT::Caching', nextSlides.length, 'slides in background');

				// Start caching each slide
				this.cachingQueue = [...nextSlides];
				this.cacheNextSlide();
			} else {
				this.setState({ isCaching: false });
			}
		} catch (err) {
			console.error('BROOD::ENTERTAINMENT::ERROR_startBackgroundCaching:', err);
			this.setState({ isCaching: false });
		}
	}

	async deleteCachedSlides() {
		const { deleteSlides } = this.state;

		if (deleteSlides.length === 0) {
			console.log('BROOD::ENTERTAINMENT::No cached slides to delete');
			return;
		}

		console.log('BROOD::ENTERTAINMENT::Deleting cached slides:', deleteSlides.length);

		for (const slide of deleteSlides) {
			try {
				// Check if the slide is active in,
				// activeSlides: [],
				// cachedSlides: [],
				// previousActiveSlides: [], 
				// this.cachingQueue = [];
				if (this.state.activeSlides.some(s => s.slideID === slide.slideID)) {
					console.warn('BROOD::ENTERTAINMENT::Skipping deletion of active slide:', slide.slideID);
					continue; // Skip if still active
				}

				if (this.state.cachedSlides.some(s => s.slideID === slide.slideID)) {
					console.warn('BROOD::ENTERTAINMENT::Skipping deletion of cached slide:', slide.slideID);
					continue; // Skip if still cached
				}

				if (this.cachingQueue.some(s => s.slideID === slide.slideID)) {
					console.warn('BROOD::ENTERTAINMENT::Skipping deletion of caching slide:', slide.slideID);
					continue; // Skip if in caching queue
				}

				if (this.state.previousActiveSlides.some(s => s.slideID === slide.slideID)) {
					console.warn('BROOD::ENTERTAINMENT::Skipping deletion of previous active slide:', slide.slideID);
					continue; // Skip if in previous active slides
				}

				deleteFromCache(slide.data.src);
				console.log('BROOD::ENTERTAINMENT::Deleted slide from cache:', slide.slideID);
			} catch (err) {
				console.error('BROOD::ENTERTAINMENT::ERROR_deleting_slide_from_cache:', slide.slideID, err);
			}
		}

		// Clear the deleteSlides array after deletion
		this.setState({
			deleteSlides: []			
		});
	}

	async cacheNextSlide() {
		if (this.abortController?.signal?.aborted) {
			return;
		}

		if (this.cachingQueue.length === 0) {
			// All slides cached successfully
			const cachedSlides = [...this.cachingQueue];
			this.setState({
				cachedSlides: this.state.cachedSlides,
				isCaching: false
			});
			console.log('BROOD::ENTERTAINMENT::Background caching complete');
			return;
		}

		const slide = this.cachingQueue.shift();

		try {
			console.log('BROOD::ENTERTAINMENT::Caching slide:', slide.slideID);

			// Create asset for caching
			const asset = {
				url: slide.data.src,
				type: slide.type,
			};

			// Cache the slide
			await new Promise((resolve, reject) => {
				const timeoutId = setTimeout(() => {
					reject(new Error('Cache timeout'));
				}, 300000); // 300 second timeout

				const operationID = preCache([asset], (completedOperationID) => {
					// Successfully cached - update cache status
					
					// Clear the timeout since we succeeded
					clearTimeout(timeoutId);

					this.setState(prevState => ({
						cachedSlides: [...prevState.cachedSlides, slide]
					}));

					resolve();
				});
			});

			// Continue with next slide
			this.cacheNextSlide();

		} catch (err) {
			console.error('BROOD::ENTERTAINMENT::ERROR_caching_slide:', slide.slideID, err);

			// Continue with next slide even if this one failed
			this.cacheNextSlide();
		}
	}

	// Create a verions of cacheNextSlide for active slides
	// don't use cacheNextSlide as that puts it ins cachedSlides
	async startActiveSlideCaching(slidesToCache) {
		// Add slides to the active caching queue
		this.activeCachingQueue = [...slidesToCache];

		// Start caching without blocking
		this.cacheNextActiveSlide();
	}

	async cacheNextActiveSlide() {
		if (this.abortController?.signal?.aborted) {
			return;
		}

		if (this.activeCachingQueue.length === 0) {
			console.log('BROOD::ENTERTAINMENT::Active slide caching complete');
			return;
		}

		const slide = this.activeCachingQueue.shift();

		// Cache this slide in background (non-blocking)
		this.cacheActiveSlide(slide).then(() => {
			// Continue with next slide after this one completes
			this.cacheNextActiveSlide();
		}).catch(err => {
			console.error('BROOD::ENTERTAINMENT::Error caching active slide:', slide.slideID, err);
			// Continue with next slide even if this one failed
			this.cacheNextActiveSlide();
		});
	}

	async cacheActiveSlide(slide) {
		console.log('BROOD::ENTERTAINMENT::cacheActiveSlide:', slide.slideID);
		if (!slide || !slide.data || !slide.data.src) {
			console.warn('BROOD::ENTERTAINMENT::cacheActiveSlide called with invalid slide:', slide);
			return;
		}

		try {
			console.log('BROOD::ENTERTAINMENT::Caching active slide:', slide.slideID);

			const asset = {
				url: slide.data.src,
				type: slide.type,
			};

			await new Promise((resolve, reject) => {
				const timeoutId = setTimeout(() => {
					// If we reach here, the caching operation took too long
					console.warn('BROOD::ENTERTAINMENT::Cache operation timed out for slide:', slide.slideID);

					reject(new Error('Cache timeout'));
				}, 300000); // 300 second timeout

				const operationID = preCache([asset], (completedOperationID) => {
					// Successfully cached - update cache status					
					console.log('BROOD::ENTERTAINMENT::Slide cached successfully:', slide.slideID);

					// Clear the timeout since we succeeded
					clearTimeout(timeoutId);

					// update the slide in activeSlides and set the skip flag to false
					this.setState(prevState => {
						const updatedSlides = prevState.activeSlides.map(s => {
							if (s.slideID === slide.slideID) {
								return { ...s, skip: false }; // Mark as cached
							}
							return s;
						});

						// Update the Provider with the new active slides
						this.props.updateSlideshowProvider(this.makeProvider(updatedSlides));

						return { activeSlides: updatedSlides };
					});

					resolve();
				});
			});

			console.log('BROOD::ENTERTAINMENT::Slide cached successfully:', slide.slideID);

		} catch (err) {
			console.error('BROOD::ENTERTAINMENT::ERROR_caching_active_slide:', slide.slideID, err);
		}
	}

	makeProvider(slides = []) {
		const {
			provider = {},
			number_of_assets: maxSlides,
			order: providerOrder,
			sort: sortType,
		} = this.props;

		return {
			providerID: this.providerID,
			providerName: this.constructor.providerName, // Add the static providerName to the provider object
			options: {
				maxSlides: maxSlides,
				providerOrder: providerOrder,
				sortType: sortType || 'sequential',
			},
			is_backfill: provider.is_backfill,
			order: provider.order,
			slots: maxSlides, // VCL slots for missing slide calculation
			skipper: this.skipper.bind(this),
			afterLastSlide: this.afterLastSlide.bind(this),
			// afterNext: this.afterNext.bind(this),
			slides,
		};
	}

	skipper(slide) {
		return slide.skip;
	}

	afterLastSlide(slide) {
		// When we reach the last slide, trigger transition to cached slides
		// and start caching the next batch
		this.initUpdate().catch(err => {
			console.error('BROOD::ENTERTAINMENT::ERROR_afterLastSlide:', err);
		});
	}

	render() {
		return null;
	}
}

export default EntertainmentProviderComponent;